import dayjs, { Dayjs } from "dayjs";
import { ChipProps } from "@mui/material/Chip";
import {
  PricebookPeriod,
  PricebookPeriodCreateRequest,
  PricebookPeriodNextPrice,
  PricebookPeriodStatus,
  PricebookPeriodUpdateRequest,
} from "../../../../types/price-book.type";
import {
  PricebookPrice,
  computePricebookPrice,
} from "../../../../utils/pricebookPrice";
import { numberWithCommas } from "../../../../utils";
import { API_DATE_FORMAT } from "../constants";
import {
  PricebookLine,
  isValidOwnDiscount,
  isValidUnitPrice,
  toAmountText,
} from "../Details/lineEdits";

/**
 * How many days before its end a running period counts as ใกล้หมด, and the range the overview
 * accepts for another window. Mirrors the API's `PricebookPeriodExpiringWindowDays`.
 */
export const EXPIRING_WINDOW_DAYS = { DEFAULT: 7, MIN: 1, MAX: 365 } as const;

export const PERIOD_STATUS_LABEL: Record<PricebookPeriodStatus, string> = {
  expiring: "ใกล้หมด",
  active: "กำลังใช้",
  upcoming: "ล่วงหน้า",
  expired: "หมดแล้ว",
};

export const PERIOD_STATUS_COLOR: Record<
  PricebookPeriodStatus,
  ChipProps["color"]
> = {
  expiring: "warning",
  active: "success",
  upcoming: "info",
  expired: "default",
};

/** The handbook's order: what needs attention first. */
export const PERIOD_STATUSES: PricebookPeriodStatus[] = [
  "expiring",
  "active",
  "upcoming",
  "expired",
];

/** Where the price after a period comes from. */
export const NEXT_PRICE_SOURCE_LABEL: Record<
  PricebookPeriodNextPrice["source"],
  string
> = {
  period: "ช่วงถัดไป",
  line: "ราคาหลัก",
};

const WHOLE_DAYS_PATTERN = /^\d+$/;

export const EXPIRING_DAYS_ERROR = `กรอกจำนวนวันเต็ม ${EXPIRING_WINDOW_DAYS.MIN}–${EXPIRING_WINDOW_DAYS.MAX}`;

/** The overview's typed warning window as whole days, or null while it is not one the API takes. */
export const parseExpiringDays = (text: string): number | null => {
  if (!WHOLE_DAYS_PATTERN.test(text.trim())) return null;
  const days = Number(text);
  return days >= EXPIRING_WINDOW_DAYS.MIN && days <= EXPIRING_WINDOW_DAYS.MAX
    ? days
    : null;
};

/** Narrows a select or query value back to a period status; anything else means no filter. */
export const toPeriodStatus = (
  value: string | null,
): PricebookPeriodStatus | undefined =>
  PERIOD_STATUSES.find((status) => status === value);

const SHORT_DAY_FORMAT = "DD/MM";
const FULL_DAY_FORMAT = "DD/MM/YYYY";

/** One business day (`YYYY-MM-DD`) as "20/10/2026". */
export const formatPeriodDate = (day: string): string =>
  dayjs(day).format(FULL_DAY_FORMAT);

/** Both days of a period, e.g. "20/10/2026–31/10/2026". */
export const formatPeriodRange = (startDate: string, endDate: string): string =>
  `${formatPeriodDate(startDate)}–${formatPeriodDate(endDate)}`;

/** One business day without its year, "20/10", for chips. */
export const formatShortDate = (day: string): string =>
  dayjs(day).format(SHORT_DAY_FORMAT);

/** The chip on a product row: "ช่วงราคา 20/10–31/10". */
export const formatActivePeriodLabel = (
  startDate: string,
  endDate: string,
): string => `ช่วงราคา ${formatShortDate(startDate)}–${formatShortDate(endDate)}`;

/** "ใกล้หมดช่วง N วัน", where N counts the days left after today; 0 is the period's last day. */
export const formatExpiringLabel = (endsInDays: number): string =>
  endsInDays > 0 ? `ใกล้หมดช่วง ${endsInDays} วัน` : "หมดช่วงวันนี้";

/** The list chip: how many of a book's periods end within the warning window. */
export const formatExpiringCountLabel = (count: number): string =>
  `ใกล้หมดช่วง ${numberWithCommas(count)}`;

/** How long a running period has left, for the period tables. */
export const formatDaysLeft = (endsInDays: number): string =>
  endsInDays > 0 ? `หมดช่วงอีก ${endsInDays} วัน` : "หมดช่วงวันนี้";

/** An ended period is the record of what was sold: the API refuses to edit, extend or delete it. */
export const isPeriodEnded = (status: PricebookPeriodStatus): boolean =>
  status === "expired";

/** A period that has started keeps its first day. */
export const hasPeriodStarted = (status: PricebookPeriodStatus): boolean =>
  status !== "upcoming";

/** What a period is priced from: its line's unit price and the line's own %. */
export interface PeriodLine {
  productCode: string;
  productName: string;
  unitPrice: number;
  /** Null follows the book's %. */
  ownDiscountPercent: number | null;
}

/**
 * A stored line a period can go on, or null for a line without a unit price of its own: the API
 * refuses a period there until the line is priced.
 */
export const toPeriodLine = (line: PricebookLine): PeriodLine | null => {
  if (line.isUnmigrated || !isValidUnitPrice(line.unitPrice)) return null;
  return {
    productCode: line.productCode,
    productName: line.productName,
    unitPrice: Number(line.unitPrice),
    ownDiscountPercent:
      line.ownDiscountPercent.trim() === "" ? null : Number(line.ownDiscountPercent),
  };
};

/** The period dialog's fields; an empty price field keeps the line's value. */
export interface PeriodForm {
  productCode: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  unitPrice: string;
  discountPercent: string;
  note: string;
}

export type PeriodFormErrors = Partial<
  Record<
    "productCode" | "startDate" | "endDate" | "unitPrice" | "discountPercent" | "price",
    string
  >
>;

/** Worded as the API words the same refusals, so a message reads the same from either side. */
export const PERIOD_MESSAGE = {
  productRequired: "กรุณาเลือกสินค้า",
  startRequired: "กรุณาเลือกวันเริ่ม",
  endRequired: "กรุณาเลือกวันสิ้นสุด",
  startInPast: "วันเริ่มของช่วงราคาต้องไม่ก่อนวันนี้",
  endInPast: "วันสิ้นสุดของช่วงราคาต้องไม่ก่อนวันนี้",
  dateOrder: "วันสิ้นสุดต้องไม่ก่อนวันเริ่ม",
  priceRequired:
    "ต้องระบุราคาต่อหน่วยหรือส่วนลด % ของช่วงราคาอย่างน้อยหนึ่งอย่าง",
  unitPriceInvalid: "ราคาต่อหน่วยต้องเป็นตัวเลข ทศนิยมไม่เกิน 2 ตำแหน่ง",
  discountInvalid: "ส่วนลดต้องอยู่ระหว่าง 0–100 ทศนิยมไม่เกิน 2 ตำแหน่ง",
  extendNotLater: "วันสิ้นสุดใหม่ต้องหลังวันสิ้นสุดเดิม",
} as const;

const isBlank = (text: string): boolean => text.trim() === "";

const toNullableNumber = (text: string): number | null =>
  isBlank(text) ? null : Number(text);

const toValidDay = (day: Dayjs | null): Dayjs | null =>
  day?.isValid() ? day : null;

/** A picked day as the API takes it. */
export const formatPeriodDay = (day: Dayjs): string => day.format(API_DATE_FORMAT);

const toNote = (text: string): string | null => text.trim() || null;

/** A blank form for a new period of `productCode` ("" until one is picked). */
export const emptyPeriodForm = (productCode: string): PeriodForm => ({
  productCode,
  startDate: null,
  endDate: null,
  unitPrice: "",
  discountPercent: "",
  note: "",
});

/** The stored period, ready to edit. */
export const toPeriodForm = (period: PricebookPeriod): PeriodForm => ({
  productCode: period.product_code,
  startDate: dayjs(period.start_date),
  endDate: dayjs(period.end_date),
  unitPrice: toAmountText(period.unit_price),
  discountPercent: toAmountText(period.discount_percent),
  note: period.note ?? "",
});

const validateDates = (
  form: PeriodForm,
  today: Dayjs,
  stored: PricebookPeriod | null,
): PeriodFormErrors => {
  const errors: PeriodFormErrors = {};
  const startDate = toValidDay(form.startDate);
  const endDate = toValidDay(form.endDate);
  // A new period starts today or later; an edit is held to that only when it moves the start.
  const isStartMoved =
    !stored || !startDate || formatPeriodDay(startDate) !== stored.start_date;

  if (!startDate) errors.startDate = PERIOD_MESSAGE.startRequired;
  else if (isStartMoved && startDate.isBefore(today, "day")) {
    errors.startDate = PERIOD_MESSAGE.startInPast;
  }

  if (!endDate) errors.endDate = PERIOD_MESSAGE.endRequired;
  else if (startDate && endDate.isBefore(startDate, "day")) {
    errors.endDate = PERIOD_MESSAGE.dateOrder;
  } else if (endDate.isBefore(today, "day")) {
    errors.endDate = PERIOD_MESSAGE.endInPast;
  }
  return errors;
};

const validatePrices = (form: PeriodForm): PeriodFormErrors => {
  const errors: PeriodFormErrors = {};
  if (!isBlank(form.unitPrice) && !isValidUnitPrice(form.unitPrice)) {
    errors.unitPrice = PERIOD_MESSAGE.unitPriceInvalid;
  }
  if (!isValidOwnDiscount(form.discountPercent)) {
    errors.discountPercent = PERIOD_MESSAGE.discountInvalid;
  }
  if (isBlank(form.unitPrice) && isBlank(form.discountPercent)) {
    errors.price = PERIOD_MESSAGE.priceRequired;
  }
  return errors;
};

/**
 * What the API would refuse, checked before the request: the product, the days (`today` is the
 * Bangkok day) and at least one own price. `stored` is the period being edited, null for a new one.
 */
export const validatePeriodForm = (
  form: PeriodForm,
  today: Dayjs,
  stored: PricebookPeriod | null,
): PeriodFormErrors => ({
  ...(form.productCode ? {} : { productCode: PERIOD_MESSAGE.productRequired }),
  ...validateDates(form, today, stored),
  ...validatePrices(form),
});

/**
 * The price the period would sell at, computed exactly as the API computes it: its own unit
 * price or the line's, less its own % or the line's applied one (the line's own, else the
 * book's). Null while a typed price is not a valid number yet or the line is unknown.
 */
export const computePeriodPrice = (
  form: Pick<PeriodForm, "unitPrice" | "discountPercent">,
  line: PeriodLine | undefined,
  bookDiscountPercent: number,
): PricebookPrice | null => {
  if (!line) return null;
  if (!isBlank(form.unitPrice) && !isValidUnitPrice(form.unitPrice)) return null;
  if (!isValidOwnDiscount(form.discountPercent)) return null;
  return computePricebookPrice({
    unitPrice: toNullableNumber(form.unitPrice) ?? line.unitPrice,
    itemDiscountPercent:
      toNullableNumber(form.discountPercent) ?? line.ownDiscountPercent,
    pricebookDiscountPercent: bookDiscountPercent,
  });
};

/** Body of a new period, or null while either day is missing. */
export const buildPeriodCreateRequest = (
  form: PeriodForm,
): PricebookPeriodCreateRequest | null => {
  const startDate = toValidDay(form.startDate);
  const endDate = toValidDay(form.endDate);
  if (!startDate || !endDate) return null;
  return {
    product_code: form.productCode,
    start_date: formatPeriodDay(startDate),
    end_date: formatPeriodDay(endDate),
    unit_price: toNullableNumber(form.unitPrice),
    discount_percent: toNullableNumber(form.discountPercent),
    note: toNote(form.note),
  };
};

/**
 * Only what the edit changes, so the history records just that and an untouched start never
 * reaches the "started" rule; empty when nothing changed, null while either day is missing.
 */
export const buildPeriodUpdateRequest = (
  form: PeriodForm,
  stored: PricebookPeriod,
): PricebookPeriodUpdateRequest | null => {
  const startDate = toValidDay(form.startDate);
  const endDate = toValidDay(form.endDate);
  if (!startDate || !endDate) return null;
  const request: PricebookPeriodUpdateRequest = {};
  const start = formatPeriodDay(startDate);
  const end = formatPeriodDay(endDate);
  const unitPrice = toNullableNumber(form.unitPrice);
  const discountPercent = toNullableNumber(form.discountPercent);
  const note = toNote(form.note);
  if (start !== stored.start_date) request.start_date = start;
  if (end !== stored.end_date) request.end_date = end;
  if (unitPrice !== stored.unit_price) request.unit_price = unitPrice;
  if (discountPercent !== stored.discount_percent) {
    request.discount_percent = discountPercent;
  }
  if (note !== stored.note) request.note = note;
  return request;
};

/** Why a new last day cannot extend the period, or undefined when it can. */
export const validateExtendDate = (
  endDate: Dayjs | null,
  currentEndDate: string,
): string | undefined => {
  const day = toValidDay(endDate);
  if (!day) return PERIOD_MESSAGE.endRequired;
  if (!day.isAfter(dayjs(currentEndDate), "day")) {
    return PERIOD_MESSAGE.extendNotLater;
  }
  return undefined;
};
